import React from 'react';
import { CustomStepProps } from '../../types/survey';

export const AddictionsAssessmentResults: React.FC<CustomStepProps> = ({
  onNext,
  onBack,
  surveyData,
}) => {
  // Calculate smoking score
  const calculateSmokingScore = () => {
    const answers = surveyData?.answers || {};
    const smoking = answers.smoking;
    
    // Direct value from question (1-5 scale)
    return smoking || 0;
  };

  // Calculate alcohol score
  const calculateAlcoholScore = () => {
    const answers = surveyData?.answers || {};
    const alcohol = answers.alcohol_consumption;
    
    // Direct value from question (1-5 scale)
    return alcohol || 0;
  };

  const smokingScore = calculateSmokingScore();
  const alcoholScore = calculateAlcoholScore();
  
  const hasSmokingIssues = smokingScore < 4;
  const hasAlcoholIssues = alcoholScore < 4;

  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-blue-lightest via-white to-royal-gold-lightest flex items-center justify-center p-4">
      <div className="max-w-3xl w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section with Gradient */}
          <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/30 rounded-full mb-4">
                <svg className="w-10 h-10 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold mb-4 text-gray-800">
                Addictions Assessment Results
              </h1>
              <p className="text-xl text-gray-700">
                Your smoking and alcohol consumption analysis
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {/* Score Display */}
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-2xl p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2 text-gray-800">{smokingScore}/5</div>
                  <div className="text-sm text-gray-700">Smoking Score</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {smokingScore === 5 ? "Non-smoker" : 
                     smokingScore === 4 ? "Light smoker" :
                     smokingScore === 3 ? "Moderate smoker" :
                     smokingScore === 2 ? "Heavy smoker" : "Very heavy smoker"}
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-royal-blue-lightest to-royal-gold rounded-2xl p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2 text-gray-800">{alcoholScore}/5</div>
                  <div className="text-sm text-gray-700">Alcohol Score</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {alcoholScore === 5 ? "Non-drinker" :
                     alcoholScore === 4 ? "Occasional drinker" :
                     alcoholScore === 3 ? "Regular drinker" :
                     alcoholScore === 2 ? "Frequent drinker" : "Daily drinker"}
                  </div>
                </div>
              </div>
            </div>

            {/* Smoking Assessment */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Tobacco Use Assessment
              </h2>
              <div className={`${hasSmokingIssues ? 'bg-amber-50' : 'bg-green-50'} rounded-lg p-6`}>
                <h3 className={`text-lg font-semibold mb-3 ${hasSmokingIssues ? 'text-amber-900' : 'text-green-900'}`}>
                  {hasSmokingIssues ? 'Time to quit smoking!' : 'Excellent choice!'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {hasSmokingIssues ? (
                    <>
                      The fact that smoking causes harm is backed up by experts and warning labels on cigarette packets. 
                      Smoking is the direct cause of many comorbidities and the #1 factor affecting the length and quality 
                      of our life. Smokers are also more likely to have a lowered immunity, higher risk of depression and 
                      anxiety attacks. Despite this, quitting smoking is a challenge for most smokers. It's worth considering 
                      the actual reasons for smoking, as adequate motivation is essential for coping with the habit.
                    </>
                  ) : (
                    <>
                      We are glad you are taking care of your health and not smoking. According to scientific research, 
                      every cigarette you smoke, on average, shortens your life by 11 minutes. We wish you a successful life, 
                      longer by multiples of 11 minutes :)
                    </>
                  )}
                </p>
              </div>
              
              {hasSmokingIssues && (
                <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Support Available</h3>
                      <p className="mt-1 text-sm text-red-700">
                        If you don't know how to kick the habit and you want to stop smoking – contact us. 
                        Our team of specialists will help you get back to a healthy lifestyle.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Alcohol Assessment */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Alcohol Consumption Assessment
              </h2>
              <div className={`${hasAlcoholIssues ? 'bg-amber-50' : 'bg-green-50'} rounded-lg p-6`}>
                <h3 className={`text-lg font-semibold mb-3 ${hasAlcoholIssues ? 'text-amber-900' : 'text-green-900'}`}>
                  {hasAlcoholIssues ? 'Consider reducing alcohol intake' : 'Well done!'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {hasAlcoholIssues ? (
                    <>
                      Alcohol abuse can have complex effects on your body. Sometimes the harmful effects don't become 
                      apparent until later in life, making it difficult to intervene early and avoid future health 
                      complications. While every organ in your body is affected by drinking, some are more likely to 
                      suffer serious damage. Our specialists will advise you on the right amount for your health and lifestyle.
                    </>
                  ) : (
                    <>
                      Your results suggest that you do not drink alcohol or drink it occasionally. This will bring nothing 
                      but benefits to your body. Take care of the other aspects of a healthy lifestyle and you will be able 
                      to enjoy good health for years to come. Keep it up!
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Health Impact Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Health Impact of Addictions
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-royal-gold-lightest rounded-lg p-6">
                  <h3 className="font-semibold text-gray-800 mb-3">Smoking Effects</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      Increases cancer risk by up to 30 times
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      Damages cardiovascular system
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      Weakens immune system
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      Accelerates aging process
                    </li>
                  </ul>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-6">
                  <h3 className="font-semibold text-gray-800 mb-3">Alcohol Effects</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Liver damage and cirrhosis
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Increased risk of heart disease
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Brain function impairment
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Weakened immune response
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Expert Recommendations */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Our Experts Recommend
              </h2>
              <div className="space-y-3">
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-gold mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Quit smoking</span> - Every cigarette reduces life expectancy by 11 minutes
                    </p>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-blue mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Limit alcohol</span> - No more than 14 units per week for both men and women
                    </p>
                  </div>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-gold mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Seek support</span> - Professional help increases success rate by 300%
                    </p>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-royal-blue mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <p className="text-gray-700">
                      <span className="font-semibold">Replace habits</span> - Find healthy alternatives to manage stress
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits of Quitting Grid */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Benefits of Quitting</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">After 20 minutes</span>
                      <p className="text-xs text-gray-600 mt-1">Heart rate & blood pressure drop</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">After 12 hours</span>
                      <p className="text-xs text-gray-600 mt-1">Carbon monoxide levels normalize</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">After 1 year</span>
                      <p className="text-xs text-gray-600 mt-1">Heart disease risk cut in half</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">After 5 years</span>
                      <p className="text-xs text-gray-600 mt-1">Stroke risk equals non-smoker</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8">
              <button
                onClick={onBack}
                className="px-8 py-3 bg-white border-2 border-royal-gold text-royal-gold font-semibold rounded-lg hover:bg-royal-gold hover:text-white transition-colors"
              >
                Previous
              </button>
              <button
                onClick={onNext}
                className="px-8 py-3 bg-royal-gold text-white font-semibold rounded-lg hover:bg-royal-gold-dark transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};